import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatPrice } from '@/util/formatPrice';
import OrderSummary from './OrderSummary';
import PaymentMethod from './PaymentMethod';
import { PaymentType } from '@/queries/payments/useCreatePayment';
import { ManualPaymentRequestResponse, ManualPaymentService } from '@/api/manualPaymentService';
import { Order } from '@/api/SolarCloudApi';
import { getBuyPanelsPath } from '../route';
import ManualPaymentFormFactory from './ManualPaymentFormFactory';
import { useEnvironmentConfig, usePayPalClientToken } from '@/queries/environment/useEnvironmentConfig';

interface ManualBalanceCheckoutProps {
  request: ManualPaymentRequestResponse;
  order: Order;
  token?: string | null;
}

export default function ManualBalanceCheckout({ request, order, token }: ManualBalanceCheckoutProps) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentType>(PaymentType.Mint);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { data: env } = useEnvironmentConfig();
  const { data: paypalTokenData, isLoading: isPayPalTokenLoading, isError: isPayPalTokenError } = usePayPalClientToken();
  const routePrefix = getBuyPanelsPath(window.location.pathname, '');

  const clientId = env?.paypal?.clientId ?? '';
  const showPayPal = !isPayPalTokenError && !!paypalTokenData?.clientToken && !!clientId;

  const availableMethods = useMemo(
    () => [
      PaymentType.Mint,
      PaymentType.Apple,
      PaymentType.Google,
      PaymentType.Afterpay,
      PaymentType.Klarna,
      PaymentType.PayTo,
    ].concat(showPayPal ? [PaymentType.PayPal, PaymentType.PayPalPayIn4] : []),
    [showPayPal]
  );

  useEffect(() => {
    if (!availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedMethod]);

  const handleHostedCheckout = async () => {
    setIsRedirecting(true);
    try {
      const { checkoutUrl } = await ManualPaymentService.createSession(request.requestId, token ?? undefined);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create manual checkout session', error);
      setIsRedirecting(false);
      window.location.assign(
        `${routePrefix}/manual-payment?requestId=${encodeURIComponent(request.requestId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`
      );
    }
  };

  if (request.state === 'Paid') {
    return (
      <div className="mx-auto max-w-screen-md p-6">
        <Card className="bg-brand-neutral-800 border-transparent shadow-none">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Balance Payment</CardTitle>
            <CardDescription>This balance is already paid.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-brand-neutral-700/50 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order Number</span>
                <span className="font-medium">{request.orderNumber}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-muted-foreground">Amount Paid</span>
                <span className="font-bold text-brand-orange-500">{formatPrice(request.amount)}</span>
              </div>
            </div>
            <Button asChild className="w-full">
              <Link to="/dashboard">Go to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isPayPalTokenLoading) {
    return null;
  }

  const checkout = (
    <div className="mx-auto max-w-screen-lg p-6 space-y-6">
      <Card className="bg-brand-neutral-800 border-transparent shadow-none">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Balance Payment</CardTitle>
          <CardDescription>Complete the remaining payment for order {request.orderNumber}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg bg-brand-neutral-700/50 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Number</span>
              <span className="font-medium">{request.orderNumber}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount Due</span>
              <span className="font-bold text-lg text-brand-orange-500">{formatPrice(request.amount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Options</span>
              <span className="font-medium">Choose from the available methods below</span>
            </div>
          </div>

          <PaymentMethod
            selectedMethod={selectedMethod}
            onMethodSelect={(method) => {
              if (method) {
                setSelectedMethod(method);
              }
            }}
            availableMethods={availableMethods}
            renderSelectedContent={(method) => (
              <div className="space-y-4 pt-2">
                <ManualPaymentFormFactory
                  selectedMethod={method}
                  requestUuid={request.requestId}
                  token={token ?? undefined}
                  beforePayment={undefined}
                  onHostedCheckout={handleHostedCheckout}
                  isRedirecting={isRedirecting}
                  setIsRedirecting={setIsRedirecting}
                />
              </div>
            )}
          />

          <OrderSummary order={order} summaryLabel="Balance" summaryAmount={request.amount} />

          <p className="text-center text-xs text-muted-foreground">
            Secure payment processed by Stripe. Your payment details are never stored on our servers.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  if (showPayPal) {
    return (
      <PayPalScriptProvider
        options={{
          clientId,
          'client-id': clientId,
          currency: 'AUD',
          'data-client-token': paypalTokenData?.clientToken ?? '',
          components: 'buttons',
          intent: 'capture',
          'disable-funding': 'card',
        }}
      >
        {checkout}
      </PayPalScriptProvider>
    );
  }

  return checkout;
}
